/**
 * Theme Store
 *
 * Zustand store for managing theme colors per dog
 * Persists across pages and sessions
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Converts a hex color to HSL values
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 50 };

  const r = parseInt(result[1] ?? '0', 16) / 255;
  const g = parseInt(result[2] ?? '0', 16) / 255;
  const b = parseInt(result[3] ?? '0', 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/**
 * Adjusts a hex color's lightness
 */
function adjustLightness(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  const newL = Math.max(0, Math.min(100, l + amount));
  return `hsl(${h}, ${s}%, ${newL}%)`;
}

/**
 * Generates a complete theme object from a hex color
 */
export function generateCustomTheme(hex: string): CustomThemeColor {
  const { h, s, l } = hexToHsl(hex);
  const lighter = `hsl(${h}, ${s}%, ${Math.min(95, l + 40)}%)`;
  const darker = `hsl(${h}, ${s}%, ${Math.max(20, l - 10)}%)`;

  return {
    id: 'custom',
    name: 'Personalizada',
    hex,
    gradient: `linear-gradient(135deg, ${hex} 0%, ${darker} 100%)`,
    solid: hex,
    text: darker,
    bg: lighter,
    border: hex,
    shadow: `${hex}33`,
    buttonGradient: `linear-gradient(135deg, ${adjustLightness(hex, 5)} 0%, ${hex} 50%, ${darker} 100%)`,
    buttonShadow: `${hex}40`,
    ring: hex,
    preview: hex,
    isCustom: true,
  };
}

// Custom theme type (uses inline styles instead of Tailwind classes)
export interface CustomThemeColor {
  id: 'custom';
  name: string;
  hex: string;
  gradient: string;
  solid: string;
  text: string;
  bg: string;
  border: string;
  shadow: string;
  buttonGradient: string;
  buttonShadow: string;
  ring: string;
  preview: string;
  isCustom: true;
}

// Available theme colors
export const THEME_COLORS = {
  primary: {
    id: 'primary',
    name: 'Verde (Padrao)',
    gradient: 'from-primary-500 via-primary-500 to-primary-600',
    solid: 'bg-primary-500',
    text: 'text-primary-600',
    textHover: 'hover:text-primary-600',
    bg: 'bg-primary-100',
    bgHover: 'hover:bg-primary-100/80',
    border: 'border-primary-500',
    shadow: 'shadow-primary-500/20',
    buttonGradient: 'from-primary-400 via-primary-500 to-primary-600',
    buttonShadow: 'shadow-primary-500/25',
    ring: 'ring-primary-400',
    preview: '#10B981', // Tailwind emerald-500
  },
  blue: {
    id: 'blue',
    name: 'Azul',
    gradient: 'from-blue-500 via-blue-500 to-blue-600',
    solid: 'bg-blue-500',
    text: 'text-blue-600',
    textHover: 'hover:text-blue-600',
    bg: 'bg-blue-100',
    bgHover: 'hover:bg-blue-100/80',
    border: 'border-blue-500',
    shadow: 'shadow-blue-500/20',
    buttonGradient: 'from-blue-400 via-blue-500 to-blue-600',
    buttonShadow: 'shadow-blue-500/25',
    ring: 'ring-blue-400',
    preview: '#3B82F6', // Tailwind blue-500
  },
  purple: {
    id: 'purple',
    name: 'Roxo',
    gradient: 'from-purple-500 via-purple-500 to-purple-600',
    solid: 'bg-purple-500',
    text: 'text-purple-600',
    textHover: 'hover:text-purple-600',
    bg: 'bg-purple-100',
    bgHover: 'hover:bg-purple-100/80',
    border: 'border-purple-500',
    shadow: 'shadow-purple-500/20',
    buttonGradient: 'from-purple-400 via-purple-500 to-purple-600',
    buttonShadow: 'shadow-purple-500/25',
    ring: 'ring-purple-400',
    preview: '#8B5CF6', // Tailwind purple-500
  },
  pink: {
    id: 'pink',
    name: 'Rosa',
    gradient: 'from-pink-500 via-pink-500 to-pink-600',
    solid: 'bg-pink-500',
    text: 'text-pink-600',
    textHover: 'hover:text-pink-600',
    bg: 'bg-pink-100',
    bgHover: 'hover:bg-pink-100/80',
    border: 'border-pink-500',
    shadow: 'shadow-pink-500/20',
    buttonGradient: 'from-pink-400 via-pink-500 to-pink-600',
    buttonShadow: 'shadow-pink-500/25',
    ring: 'ring-pink-400',
    preview: '#EC4899', // Tailwind pink-500
  },
  orange: {
    id: 'orange',
    name: 'Laranja',
    gradient: 'from-orange-500 via-orange-500 to-orange-600',
    solid: 'bg-orange-500',
    text: 'text-orange-600',
    textHover: 'hover:text-orange-600',
    bg: 'bg-orange-100',
    bgHover: 'hover:bg-orange-100/80',
    border: 'border-orange-500',
    shadow: 'shadow-orange-500/20',
    buttonGradient: 'from-orange-400 via-orange-500 to-orange-600',
    buttonShadow: 'shadow-orange-500/25',
    ring: 'ring-orange-400',
    preview: '#F97316', // Tailwind orange-500
  },
  amber: {
    id: 'amber',
    name: 'Amarelo',
    gradient: 'from-amber-500 via-amber-500 to-amber-600',
    solid: 'bg-amber-500',
    text: 'text-amber-600',
    textHover: 'hover:text-amber-600',
    bg: 'bg-amber-100',
    bgHover: 'hover:bg-amber-100/80',
    border: 'border-amber-500',
    shadow: 'shadow-amber-500/20',
    buttonGradient: 'from-amber-400 via-amber-500 to-amber-600',
    buttonShadow: 'shadow-amber-500/25',
    ring: 'ring-amber-400',
    preview: '#F59E0B', // Tailwind amber-500
  },
  red: {
    id: 'red',
    name: 'Vermelho',
    gradient: 'from-red-500 via-red-500 to-red-600',
    solid: 'bg-red-500',
    text: 'text-red-600',
    textHover: 'hover:text-red-600',
    bg: 'bg-red-100',
    bgHover: 'hover:bg-red-100/80',
    border: 'border-red-500',
    shadow: 'shadow-red-500/20',
    buttonGradient: 'from-red-400 via-red-500 to-red-600',
    buttonShadow: 'shadow-red-500/25',
    ring: 'ring-red-400',
    preview: '#EF4444', // Tailwind red-500
  },
  teal: {
    id: 'teal',
    name: 'Teal',
    gradient: 'from-teal-500 via-teal-500 to-teal-600',
    solid: 'bg-teal-500',
    text: 'text-teal-600',
    textHover: 'hover:text-teal-600',
    bg: 'bg-teal-100',
    bgHover: 'hover:bg-teal-100/80',
    border: 'border-teal-500',
    shadow: 'shadow-teal-500/20',
    buttonGradient: 'from-teal-400 via-teal-500 to-teal-600',
    buttonShadow: 'shadow-teal-500/25',
    ring: 'ring-teal-400',
    preview: '#14B8A6', // Tailwind teal-500
  },
  indigo: {
    id: 'indigo',
    name: 'Indigo',
    gradient: 'from-indigo-500 via-indigo-500 to-indigo-600',
    solid: 'bg-indigo-500',
    text: 'text-indigo-600',
    textHover: 'hover:text-indigo-600',
    bg: 'bg-indigo-100',
    bgHover: 'hover:bg-indigo-100/80',
    border: 'border-indigo-500',
    shadow: 'shadow-indigo-500/20',
    buttonGradient: 'from-indigo-400 via-indigo-500 to-indigo-600',
    buttonShadow: 'shadow-indigo-500/25',
    ring: 'ring-indigo-400',
    preview: '#6366F1', // Tailwind indigo-500
  },
  slate: {
    id: 'slate',
    name: 'Cinza',
    gradient: 'from-slate-500 via-slate-500 to-slate-600',
    solid: 'bg-slate-500',
    text: 'text-slate-600',
    textHover: 'hover:text-slate-600',
    bg: 'bg-slate-100',
    bgHover: 'hover:bg-slate-100/80',
    border: 'border-slate-500',
    shadow: 'shadow-slate-500/20',
    buttonGradient: 'from-slate-400 via-slate-500 to-slate-600',
    buttonShadow: 'shadow-slate-500/25',
    ring: 'ring-slate-400',
    preview: '#64748B', // Tailwind slate-500
  },
} as const;

export type ThemeColorId = keyof typeof THEME_COLORS;
export type ThemeColor = (typeof THEME_COLORS)[ThemeColorId];
export type ThemeColorOrCustom = ThemeColor | CustomThemeColor;

// Theme selection can be a predefined ID or a custom hex color
export type ThemeSelection = ThemeColorId | { custom: string };

interface ThemeState {
  // Map of dogId -> theme selection (either predefined ID or custom hex)
  dogThemes: Record<string, ThemeSelection>;
  // Set theme for a specific dog (predefined)
  setDogTheme: (dogId: string, colorId: ThemeColorId) => void;
  // Set custom theme for a specific dog
  setDogCustomTheme: (dogId: string, hex: string) => void;
  // Get theme for a specific dog (defaults to 'primary')
  getDogTheme: (dogId: string) => ThemeColorOrCustom;
  // Get theme color ID for a specific dog (returns 'custom' for custom themes)
  getDogThemeId: (dogId: string) => ThemeColorId | 'custom';
  // Get custom hex if set
  getDogCustomHex: (dogId: string) => string | null;
  // Check if dog has custom theme
  isCustomTheme: (dogId: string) => boolean;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      dogThemes: {},
      setDogTheme: (dogId, colorId) =>
        set((state) => ({
          dogThemes: { ...state.dogThemes, [dogId]: colorId },
        })),
      setDogCustomTheme: (dogId, hex) =>
        set((state) => ({
          dogThemes: { ...state.dogThemes, [dogId]: { custom: hex } },
        })),
      getDogTheme: (dogId) => {
        const selection = get().dogThemes[dogId] ?? 'primary';
        if (typeof selection === 'object' && 'custom' in selection) {
          return generateCustomTheme(selection.custom);
        }
        return THEME_COLORS[selection];
      },
      getDogThemeId: (dogId) => {
        const selection = get().dogThemes[dogId] ?? 'primary';
        if (typeof selection === 'object' && 'custom' in selection) {
          return 'custom';
        }
        return selection;
      },
      getDogCustomHex: (dogId) => {
        const selection = get().dogThemes[dogId];
        if (typeof selection === 'object' && 'custom' in selection) {
          return selection.custom;
        }
        return null;
      },
      isCustomTheme: (dogId) => {
        const selection = get().dogThemes[dogId];
        return typeof selection === 'object' && 'custom' in selection;
      },
    }),
    {
      name: 'aiodog-dog-themes',
    }
  )
);

// Helper hook to get current dog's theme
export function useCurrentDogTheme(dogId: string | null | undefined): ThemeColorOrCustom {
  const getDogTheme = useThemeStore((state) => state.getDogTheme);
  return getDogTheme(dogId ?? '');
}

// Type guard to check if theme is custom
export function isCustomThemeColor(theme: ThemeColorOrCustom): theme is CustomThemeColor {
  return 'isCustom' in theme && theme.isCustom === true;
}

/**
 * Helper interface for theme styles that can be applied to components
 */
export interface ThemeStyles {
  /** CSS classes for gradient background (empty if custom) */
  gradientClass: string;
  /** Inline style for gradient background (used for custom colors) */
  gradientStyle: React.CSSProperties | undefined;
  /** CSS classes for solid background (empty if custom) */
  solidClass: string;
  /** Inline style for solid background (used for custom colors) */
  solidStyle: React.CSSProperties | undefined;
  /** CSS classes for text color (empty if custom) */
  textClass: string;
  /** Inline style for text color (used for custom colors) */
  textStyle: React.CSSProperties | undefined;
  /** CSS classes for background (light) (empty if custom) */
  bgClass: string;
  /** Inline style for background (used for custom colors) */
  bgStyle: React.CSSProperties | undefined;
  /** CSS classes for border (empty if custom) */
  borderClass: string;
  /** Inline style for border (used for custom colors) */
  borderStyle: React.CSSProperties | undefined;
  /** CSS classes for shadow (empty if custom) */
  shadowClass: string;
  /** Inline style for shadow (used for custom colors) */
  shadowStyle: React.CSSProperties | undefined;
  /** The preview color (hex) - always available */
  preview: string;
  /** Theme name */
  name: string;
  /** Whether this is a custom theme */
  isCustom: boolean;
}

/**
 * Get theme styles that can be applied to components.
 * Returns both class names and inline styles - use classes for predefined themes,
 * use inline styles for custom themes.
 */
export function getThemeStyles(theme: ThemeColorOrCustom): ThemeStyles {
  if (isCustomThemeColor(theme)) {
    // Custom theme - use inline styles
    return {
      gradientClass: '',
      gradientStyle: { background: theme.gradient },
      solidClass: '',
      solidStyle: { backgroundColor: theme.solid },
      textClass: '',
      textStyle: { color: theme.text },
      bgClass: '',
      bgStyle: { backgroundColor: theme.bg },
      borderClass: '',
      borderStyle: { borderColor: theme.border },
      shadowClass: '',
      shadowStyle: { boxShadow: `0 4px 14px 0 ${theme.shadow}` },
      preview: theme.preview,
      name: theme.name,
      isCustom: true,
    };
  }

  // Predefined theme - use Tailwind classes
  return {
    gradientClass: `bg-gradient-to-r ${theme.gradient}`,
    gradientStyle: undefined,
    solidClass: theme.solid,
    solidStyle: undefined,
    textClass: theme.text,
    textStyle: undefined,
    bgClass: theme.bg,
    bgStyle: undefined,
    borderClass: theme.border,
    borderStyle: undefined,
    shadowClass: `shadow-lg ${theme.shadow}`,
    shadowStyle: undefined,
    preview: theme.preview,
    name: theme.name,
    isCustom: false,
  };
}

/**
 * Hook to get theme styles for a dog
 */
export function useDogThemeStyles(dogId: string | null | undefined): ThemeStyles {
  const theme = useCurrentDogTheme(dogId);
  return getThemeStyles(theme);
}
